import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Breadcrumbs, Link } from '@material-ui/core';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { Box } from '@chakra-ui/react';

import { TableAndModal, Form, TabPanelWrapper } from '..';
import LoadingFieldDropdown from './LoadingFieldDropdown';
import walkthroughIds from './walkthroughIds';

/*
 * A Edit Form that renders the children passed into it from state
 * this is rendered in a modal with data from the modal State
 */
export const EditForm = ({
    modalUpload,
    modalContent,
    modalValidate,
    modalFetch,
    modalAdd,
    modalUpdate,
}) => {
    const dispatch = useDispatch();

    // loading state
    const { loading: modalLoading } = useSelector((state) => {
        return {
            ...state.modal,
        };
    });

    // child data from table state
    const { child } = useSelector((state) => {
        return { ...state.table };
    });

    // use effect that dispatches a open modal with the child data whenever the child changes
    useEffect(() => {
        if (child?.id) {
            dispatch({
                type: 'OPEN_MODAL',
                payload: {
                    mode: 'Edit',
                    data: child,
                    show: true,
                    loading: true,
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [child]);

    // returns a form with the modal State from above
    return (
        <Form
            reduxState="modal"
            loading={modalLoading}
            validateOnSubmit={modalValidate}
            reduxPrefix=""
            formUpdate={modalUpdate}
            formAdd={modalAdd}
            mode="Edit"
            formFetch={modalFetch}
            formUpload={modalUpload}
            formContent={modalContent}
        />
    );
};

/*
 * A component that takes in title, tabs, loadingfielddropdown and presents them as a tabbed series of forms
 */
const MultiChild = ({
    title,
    tabs,
    loadingFieldDropdown = {},
    showBreadcrumbs = true,
    ...rest
}) => {
    const dispatch = useDispatch();

    // table state
    const { child, parent } = useSelector((state) => {
        return {
            ...state.table,
        };
    });

    // breadcrum from state
    const { breadcrumb } = useSelector((state) => {
        return { ...state.view };
    });

    // function for tabChange dispatch
    const onTabChange = (e, v) => {
        if (v !== breadcrumb) {
            dispatch({
                type: 'SWITCH_CHILD_TABLE',
                payload: v,
            });
        }
    };

    /*
     * Returns a tabbed page using the EditForm component above if a child exists
     * else render a TableAndModal.jsx
     */
    return (
        <>
            {child ? (
                <>
                    {showBreadcrumbs && (
                        <Breadcrumbs aria-label="breadcrumb">
                            <Link
                                href="/"
                                color="inherit"
                                onClick={(e) => {
                                    e.preventDefault();
                                    dispatch({
                                        type: 'RESET_TABLE_MULTICHILD',
                                        payload: 'all',
                                    });
                                }}
                                data-walkthroughid={
                                    walkthroughIds.ufBreadcrumbs
                                }>
                                All {title}
                            </Link>
                            <div style={{ minWidth: 500 }}>
                                <LoadingFieldDropdown
                                    dropDownValue="id"
                                    displayField="name"
                                    onChange={(v) => {
                                        // v may be string or number, match type
                                        const childData = (parent || []).find(
                                            (parentValue) =>
                                                String(parentValue.id) ===
                                                String(v),
                                        );
                                        if (childData) {
                                            // Legacy MUI MultiChild: set parent, open new child, reset tab/breadcrumb
                                            dispatch({
                                                type: 'CHANGE_PARENT_TABLE',
                                                payload: parent,
                                            });
                                            dispatch({
                                                type: 'OPEN_CHILD_TABLE',
                                                payload: {
                                                    ...childData,
                                                    tab: 0,
                                                    parent,
                                                },
                                            });
                                            dispatch({
                                                type: 'SWITCH_CHILD_TABLE',
                                                payload: 0,
                                            });
                                            dispatch({
                                                type: 'CHANGE_BREADCRUMB_VIEW',
                                                payload: 0,
                                            });
                                        }
                                    }}
                                    searchable
                                    noEmptyOption
                                    fieldValue={
                                        child &&
                                        child.id !== undefined &&
                                        child.id !== null
                                            ? String(child.id)
                                            : ''
                                    }
                                    dataWalkthroughid={
                                        walkthroughIds.ufDropdown
                                    }
                                    staticEnum={
                                        loadingFieldDropdown?.staticEnum ||
                                        parent ||
                                        []
                                    }
                                    {...loadingFieldDropdown}
                                />
                            </div>
                        </Breadcrumbs>
                    )}

                    <Box
                        sx={{
                            '& .MuiTabs-indicator': {
                                backgroundColor: 'brand.500',
                            },
                        }}>
                        <Tabs value={breadcrumb} onChange={onTabChange}>
                            {tabs.map(({ label, details }, index) => (
                                <Tab
                                    key={index}
                                    label={
                                        details
                                            ? (label ?? `${title} Details`)
                                            : label
                                    }
                                    data-walkthroughid={`/tab/${title}/${index}`}
                                />
                            ))}
                        </Tabs>
                        {tabs.map(({ component, details }, index) => (
                            <TabPanelWrapper
                                key={index}
                                index={index}
                                breadcrumb={breadcrumb}>
                                {details ? <EditForm {...rest} /> : component}
                            </TabPanelWrapper>
                        ))}
                    </Box>
                </>
            ) : (
                <TableAndModal title={title} {...rest} />
            )}
        </>
    );
};

export default MultiChild;
